import { useAuth } from '@/context/Auth';
import { isNil, isNull } from 'lodash/fp';
import React from 'react';
import { getContact, updateContact } from '@/services';
import { useMessage } from '../Message';
import { ContactInfo } from './Contact.types';
import { PortalRegistrationStatuses } from './useUserRegistrationStatus';
import { useUserRedirection } from '@/context/Contact/useUserRedirection';

const parseToString = (value: undefined | string | number, defaultValue = null): string | null => {
  if (isNil(value)) return defaultValue;
  return String(value);
};

export const resolveContactInfo = (data: Record<string, any>) => {
  const contactInfo: ContactInfo = {
    fullName: `${data?.firstname} ${data?.lastname}`,
    firstName: data?.firstname,
    lastName: data?.lastname,
    contactId: data?.contactid,
    webRoles: data?.webRoles,
    accountType: parseToString(data?.customertypecode),
    portalRegistrationStatus: parseToString(data?.gen_portaluserregistrationstatus),
    portalPrivacyConsent: parseToString(data?.gen_portalprivacyconsent),
    portalNotificationSettings: data?.gen_portalnotificationsettings || null,
    betaParticipantStatus: data?.gen_betaparticipantstatus ?? null,
    ...data,
  };
  return contactInfo;
};

export const useContactManager = () => {
  const { isAuthorized, isForbidden: isAuthForbidden } = useAuth();
  const [contactInfo, setContactInfo] = React.useState<ContactInfo | null>(null);
  const [isLoading, setIsLoading] = React.useState(Boolean(isAuthorized));
  const [error, setError] = React.useState<string | null>(null);
  const message = useMessage();
  const { handleRegistrationDirection } = useUserRedirection();
  const isContactForbidden = React.useMemo(() => {
    const { portalRegistrationStatus, betaParticipantStatus } = contactInfo || {};
    const { PENDING, REGISTERED, APPROVED, DENIED } = PortalRegistrationStatuses;
    const restrictedStatuses = [PENDING, REGISTERED, DENIED];
    const isNonBetaUser = !betaParticipantStatus && portalRegistrationStatus === APPROVED;
    return (
      isAuthForbidden ||
      restrictedStatuses.includes(portalRegistrationStatus as any) ||
      isNonBetaUser
    );
  }, [contactInfo, isAuthForbidden]);
  // const [isContactForbidden, setIsContactForbidden] = React.useState(isAuthForbidden);

  /**
   * Load Contact Info
   */
  const loadContactInfo = React.useCallback(async () => {
    // setContactInfo(null);
    setIsLoading(true);

    try {
      const contact = await getContact();
      const resolvedContactInfo = resolveContactInfo(contact);
      const { portalRegistrationStatus, betaParticipantStatus } = resolvedContactInfo;

      await handleRegistrationDirection({
        portalRegistrationStatus,
        betaParticipantStatus,
      });

      setContactInfo(resolvedContactInfo);
    } catch (e) {
      setError((e as any).message);
    } finally {
      setIsLoading(false);
    }
  }, [handleRegistrationDirection]);

  /**
   * Clear Contact Info
   */
  const clearContactInfo = React.useCallback(() => {
    setContactInfo(null);
  }, [setContactInfo]);

  /**
   * Update Contact Info
   *
   */
  const updateContactInfo = React.useCallback(
    async (data: Record<string, any>, updateOnServer = false): Promise<ContactInfo | null> => {
      const {
        fullname: fullName,
        firstname: firstName,
        lastname: lastName,
        contactid: contactId,
        customertypecode: accountType,
        webRoles,
        ...rest
      } = data;

      const nextContactInfo: ContactInfo = {
        ...(contactInfo || {}),
        fullName: fullName || contactInfo?.fullName,
        firstName: firstName || contactInfo?.firstName,
        lastName: lastName || contactInfo?.lastName,
        contactId: contactId || contactInfo?.contactId,
        accountType: accountType || contactInfo?.accountType,
        webRoles: webRoles || contactInfo?.webRoles,
        ...rest,
      };

      if (updateOnServer) {
        setIsLoading(true);

        try {
          await updateContact({ values: data });
          setContactInfo(nextContactInfo);
        } catch (e) {
          message.error({ message: (e as any).message || 'An error occurred.' });
          return await Promise.reject(e);
        } finally {
          setIsLoading(false);
        }
      }

      return Promise.resolve(nextContactInfo);
    },
    [contactInfo, message, setContactInfo],
  );

  React.useEffect(() => {
    console.log('isAuthorized, isAuthForbidden', isAuthorized, isAuthForbidden);

    if (isNull(isAuthorized)) return;

    if (isAuthorized || isAuthForbidden) {
      loadContactInfo();
      return;
    }

    clearContactInfo();
  }, [loadContactInfo, isAuthorized, isAuthForbidden, clearContactInfo]);

  return {
    contactInfo,
    loadContactInfo,
    clearContactInfo,
    updateContactInfo,
    error,
    isLoading,
    isForbidden: isContactForbidden,
  };
};
